// App-level page-view instrumentation, emitting page_viewed only.
//
// Mount once near the top of the app tree (inside the router + redux Provider).
// Fires a normalized, token-scrubbed `page_viewed` on every route change for
// authenticated users, and keeps the analytics tracker's context (workspace /
// market / impersonation) in sync with redux.
//
// page_engaged (scroll depth + engaged time) is a fast-follow slice.

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { track, setAnalyticsContext, flushAnalytics } from './index';
import { normalizePath, pageName } from './routes';
import { startPage, endPage, markHidden, markVisible } from './engagement';

// The store is JS/untyped; select defensively.
type Rootish = {
  auth?: {
    isAuthenticated?: boolean;
    isImpersonating?: boolean;
    id_client?: string | null;
    selectedWorkspace?: { key?: string } | null;
  };
  filters?: { currentCountry?: string | null };
};

export function usePageViews(): void {
  const location = useLocation();
  const idClient = useSelector(
    (s: Rootish) => s.auth?.selectedWorkspace?.key ?? s.auth?.id_client ?? null,
  );
  const country = useSelector((s: Rootish) => s.filters?.currentCountry ?? null);
  const impersonating = useSelector((s: Rootish) => s.auth?.isImpersonating ?? false);
  const isAuthenticated = useSelector((s: Rootish) => s.auth?.isAuthenticated ?? false);
  const prevPage = useRef<string | null>(null);

  // Keep the tracker context current. Declared before the page-view effect so on
  // mount it runs first (React runs effects top-to-bottom) — the first
  // page_viewed already sees the right impersonation flag.
  useEffect(() => {
    setAnalyticsContext({ idClient, country, impersonating });
  }, [idClient, country, impersonating]);

  useEffect(() => {
    if (!isAuthenticated) return; // don't track public / logged-out navigation
    const page = normalizePath(location.pathname);
    // Debounce to the next tick so a synchronous redirect burst (e.g. "/" →
    // "/ai", or an email deep-link redirect) collapses to the FINAL landing
    // page: the intermediate route's timer is cleared by this effect's cleanup
    // before it fires, so it never emits a phantom page_viewed or pollutes the
    // real page's referrer_page. Human navigations (100s of ms apart) each fire.
    const id = setTimeout(() => {
      endPage(); // emit page_engaged for the page being left
      track(
        'page_viewed',
        {
          page_name: pageName(page),
          referrer_page: prevPage.current ?? undefined,
        },
        { page },
      );
      startPage(page); // begin engagement accounting for the new page
      prevPage.current = page;
    }, 0);
    return () => clearTimeout(id);
    // Intentionally keyed on pathname only — query/hash changes aren't page views.
     
  }, [location.pathname, isAuthenticated]);

  // Engagement lifecycle: pause/resume the visible-time clock on tab
  // visibility, and flush the final page_engaged on unload / unmount. Bound
  // once — the handlers are safe no-ops when no page is being accounted.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') markHidden();
      else markVisible();
    };
    const onPageHide = () => {
      endPage(); // enqueue the final page_engaged...
      void flushAnalytics(); // ...then flush it (keepalive) before the tab dies
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      endPage(); // SPA teardown
    };
  }, []);
}
